import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ExternalLink, PackageSearch, Plug, RefreshCw, ShoppingCart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { TenantSummary } from '@forq/contracts';
import {
  needsPartsReview,
  supplierProviderLabels,
  supplierPurchaseOrderStatusLabels,
  type PartsGateway,
  type PartsOrderBucket,
  type PartsOverview,
  type SupplierConnectionRecord,
  type SupplierProvider,
  type SupplierPurchaseOrderRecord,
  type SupplierPurchaseOrderStatus,
} from '@forq/parts-data';
import { EmptyState, StatusPill, Tabs, WorkspacePage, type StatusTone, type TabItem } from '@forq/ui';

/**
 * Ersatzteile -- der Überblick.
 *
 * Hier wird NICHT bestellt. Es gibt bewusst keinen Knopf „Neue Bestellung": eine Bestellung
 * entsteht ausschließlich im Vorgang, sonst wäre die Pflichtzuordnung eine Frage der Disziplin.
 * Diese Seite zeigt, was daraus geworden ist -- und sammelt die Fälle, bei denen jemand
 * nachfassen muss.
 */

interface PartsPageProps {
  readonly tenant: TenantSummary;
  readonly mode: 'demo' | 'supabase';
  readonly permissions: ReadonlySet<string>;
  readonly enabledFeatureIds: readonly string[];
  readonly gateway: PartsGateway;
  /** Läuft der Bestelldienst des FORQ-Servers? */
  readonly orderingAvailable: boolean;
}

type PartsSection = 'orders' | 'review' | 'suppliers';

const statusTone: Readonly<Record<SupplierPurchaseOrderStatus, StatusTone>> = {
  draft: 'neutral',
  validating: 'info',
  validated: 'info',
  submitting: 'progress',
  ordered: 'progress',
  confirmed: 'progress',
  'partially-delivered': 'warning',
  delivered: 'done',
  failed: 'danger',
  'requires-review': 'warning',
  'unknown-external-state': 'danger',
  cancelled: 'neutral',
};

const emptyOverview: PartsOverview = {
  draftCount: 0,
  orderedCount: 0,
  reviewCount: 0,
  openReceiptCount: 0,
};

function money(value: number, currency = 'EUR'): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(value);
}

function messageOf(error: unknown): string {
  return error instanceof Error && error.message
    ? error.message
    : 'Die Ersatzteilbestellungen konnten nicht geladen werden.';
}

export function PartsPage({
  tenant,
  mode,
  permissions,
  enabledFeatureIds,
  gateway,
  orderingAvailable,
}: PartsPageProps) {
  const navigate = useNavigate();
  const availableSections = useMemo(
    () =>
      [
        { id: 'orders' as const, feature: 'parts.orders', label: 'Bestellungen', icon: ShoppingCart },
        { id: 'review' as const, feature: 'parts.review', label: 'Klärfälle', icon: AlertTriangle },
        { id: 'suppliers' as const, feature: 'parts.suppliers', label: 'Lieferantenanbindung', icon: Plug },
      ].filter((section) => enabledFeatureIds.includes(section.feature)),
    [enabledFeatureIds],
  );

  const [activeSection, setActiveSection] = useState<PartsSection>(availableSections[0]?.id ?? 'orders');
  const [overview, setOverview] = useState<PartsOverview>(emptyOverview);
  const [orders, setOrders] = useState<readonly SupplierPurchaseOrderRecord[]>([]);
  const [connections, setConnections] = useState<readonly SupplierConnectionRecord[]>([]);
  const [bucket, setBucket] = useState<PartsOrderBucket>('open');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const mayAdmin = permissions.has('parts.admin');
  const mayReconcile = permissions.has('parts.reconcile');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [kennzahlen, liste] = await Promise.all([
        gateway.getOverview(tenant.id),
        gateway.listOrders({
          tenantId: tenant.id,
          bucket: activeSection === 'review' ? 'review' : bucket,
          query,
          pageSize: 100,
        }),
      ]);
      setOverview(kennzahlen);
      setOrders(liste.items);
      if (mayAdmin) setConnections(await gateway.listConnections(tenant.id));
    } catch (loadError) {
      setError(messageOf(loadError));
    } finally {
      setLoading(false);
    }
  }, [gateway, tenant.id, bucket, query, activeSection, mayAdmin]);

  useEffect(() => {
    void load();
  }, [load]);

  const refresh = useCallback(
    async (purchaseOrderId: string) => {
      setBusy(purchaseOrderId);
      setError(null);
      try {
        const aktuell = await gateway.refreshStatus({ tenantId: tenant.id, purchaseOrderId });
        setNotice(`Stand beim Lieferanten: ${supplierPurchaseOrderStatusLabels[aktuell.status]}.`);
        await load();
      } catch (refreshError) {
        setError(messageOf(refreshError));
      } finally {
        setBusy(null);
      }
    },
    [gateway, tenant.id, load],
  );

  const resolve = useCallback(
    async (purchaseOrderId: string, toStatus: SupplierPurchaseOrderStatus) => {
      const reason = window.prompt('Wie wurde der Fall geklärt? (wird protokolliert)');
      if (!reason) return;
      setBusy(purchaseOrderId);
      setError(null);
      try {
        await gateway.resolveReview({ tenantId: tenant.id, purchaseOrderId, toStatus, reason });
        setNotice('Der Klärfall ist entschieden und protokolliert.');
        await load();
      } catch (resolveError) {
        setError(messageOf(resolveError));
      } finally {
        setBusy(null);
      }
    },
    [gateway, tenant.id, load],
  );

  const toggleConnection = useCallback(
    async (connection: SupplierConnectionRecord, patch: Partial<SupplierConnectionRecord>) => {
      setBusy(connection.id);
      setError(null);
      try {
        await gateway.saveConnection({
          tenantId: tenant.id,
          provider: connection.provider,
          active: patch.active ?? connection.active,
          punchoutEnabled: patch.punchoutEnabled ?? connection.punchoutEnabled,
          orderingEnabled: patch.orderingEnabled ?? connection.orderingEnabled,
        });
        setConnections(await gateway.listConnections(tenant.id));
        setNotice('Die Lieferantenanbindung wurde geändert.');
      } catch (saveError) {
        setError(messageOf(saveError));
      } finally {
        setBusy(null);
      }
    },
    [gateway, tenant.id],
  );

  if (availableSections.length === 0) {
    return (
      <EmptyState
        eyebrow="Ersatzteile"
        title="Für diesen Mandanten ist kein Ersatzteilbereich freigegeben."
        description="Die Bereiche werden aus Rollen und einzelnen Funktionsrechten ermittelt."
      />
    );
  }

  const sectionTabs: readonly TabItem[] = availableSections.map((section) => {
    const Icon = section.icon;
    return { id: section.id, label: section.label, icon: <Icon size={15} /> };
  });

  const sichtbar = activeSection === 'review' ? orders.filter((entry) => needsPartsReview(entry.status)) : orders;

  return (
    <WorkspacePage
      customizeKey="parts-overview"
      eyebrow="FORQ Ersatzteile"
      title="Lieferantenbestellungen"
      description={`${tenant.name} · Bestellt wird ausschließlich aus einem Vorgang heraus`}
      actions={
        <>
          <span className={`parts-mode ${mode === 'demo' ? 'is-demo' : ''}`}>
            {mode === 'demo' ? 'Demo – Änderungen bis zum Neuladen' : 'Supabase verbunden'}
          </span>
          <button type="button" className="parts-secondary" onClick={() => void load()} disabled={loading}>
            <RefreshCw size={16} /> Aktualisieren
          </button>
        </>
      }
      toolbar={
        <Tabs
          items={sectionTabs}
          activeId={activeSection}
          onSelect={(id) => setActiveSection(id as PartsSection)}
          ariaLabel="Ersatzteilbereiche"
        />
      }
    >
      {error ? (
        <div className="parts-error" role="alert">
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)}>
            Schließen
          </button>
        </div>
      ) : null}

      {notice ? (
        <div className="parts-notice" role="status">
          <span>{notice}</span>
          <button type="button" onClick={() => setNotice(null)}>
            Schließen
          </button>
        </div>
      ) : null}

      {!orderingAvailable ? (
        <p className="parts-hint">
          Suchen und Absenden laufen über den FORQ-Server. In dieser Fassung sind Entwürfe,
          Bestandsprüfung und die Historie verfügbar.
        </p>
      ) : null}

      <div className="parts-metrics">
        <span>
          <em>{overview.draftCount}</em> Entwürfe
        </span>
        <span>
          <em>{overview.orderedCount}</em> bestellt
        </span>
        <span className={overview.reviewCount > 0 ? 'is-warning' : ''}>
          <em>{overview.reviewCount}</em> zu klären
        </span>
        <span>
          <em>{overview.openReceiptCount}</em> Wareneingang offen
        </span>
      </div>

      {activeSection !== 'suppliers' ? (
        <section className="parts-panel">
          <div className="parts-panel__heading">
            <div>
              <span className="parts-eyebrow">
                {activeSection === 'review' ? 'Nachfassen' : 'Alle Bestellungen'}
              </span>
              <h2>{activeSection === 'review' ? 'Zu klären' : 'Bestellungen'}</h2>
            </div>
            {activeSection === 'orders' ? (
              <div className="parts-filter">
                <input
                  type="search"
                  value={query}
                  placeholder="Bestell- oder Vorgangsnummer"
                  onChange={(event) => setQuery(event.target.value)}
                />
                <select value={bucket} onChange={(event) => setBucket(event.target.value as PartsOrderBucket)}>
                  <option value="open">Laufend</option>
                  <option value="review">Zu klären</option>
                  <option value="done">Abgeschlossen</option>
                  <option value="all">Alle</option>
                </select>
              </div>
            ) : null}
          </div>

          {loading ? <div className="parts-loading">Bestellungen werden geladen …</div> : null}

          {!loading && sichtbar.length === 0 ? (
            <EmptyState
              eyebrow="Ersatzteile"
              title={activeSection === 'review' ? 'Nichts zu klären.' : 'Keine Bestellungen in dieser Auswahl.'}
              description={'Eine Ersatzteilbestellung beginnt im Vorgang: Auftrag öffnen, Feld „Ersatzteile", Lieferant wählen.'}
            />
          ) : null}

          {!loading && sichtbar.length > 0 ? (
            <div className="parts-table">
              <div className="parts-table__head">
                <span>Bestellung</span>
                <span>Vorgang</span>
                <span>Lieferant</span>
                <span>Stand</span>
                <span>Beim Lieferanten</span>
                <span>Summe</span>
                <span>Aktion</span>
              </div>
              {sichtbar.map((entry) => (
                <div className="parts-table__row" key={entry.id}>
                  <span>
                    <strong>{entry.number}</strong>
                    <em>{new Date(entry.createdAt).toLocaleDateString('de-DE')}</em>
                  </span>
                  <span>
                    <button
                      type="button"
                      className="parts-link"
                      onClick={() => navigate(`/vorgaenge/${entry.workOrderNumber}`)}
                    >
                      {entry.workOrderNumber} <ExternalLink size={12} />
                    </button>
                    <em>{entry.customerName || 'ohne Kundenbezug'}</em>
                  </span>
                  <span>{supplierProviderLabels[entry.provider]}</span>
                  <span>
                    <StatusPill
                      label={supplierPurchaseOrderStatusLabels[entry.status]}
                      tone={statusTone[entry.status]}
                    />
                    {entry.reviewReason ? <em title={entry.reviewReason}>{entry.reviewReason}</em> : null}
                  </span>
                  <span>{entry.externalOrderNumber || '—'}</span>
                  <span>{money(entry.totalAmount, entry.currency)}</span>
                  <span className="parts-table__actions">
                    {needsPartsReview(entry.status) && mayReconcile ? (
                      <>
                        <button
                          type="button"
                          className="is-ghost"
                          disabled={busy !== null || !orderingAvailable}
                          onClick={() => void refresh(entry.id)}
                        >
                          Nachfragen
                        </button>
                        <button
                          type="button"
                          className="is-ghost"
                          disabled={busy !== null}
                          onClick={() => void resolve(entry.id, 'cancelled')}
                        >
                          Erledigt
                        </button>
                      </>
                    ) : null}
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </section>
      ) : null}

      {activeSection === 'suppliers' ? (
        <section className="parts-panel">
          <div className="parts-panel__heading">
            <div>
              <span className="parts-eyebrow">Technische Anbindung</span>
              <h2>Lieferanten</h2>
            </div>
          </div>

          <p className="parts-hint">
            <PackageSearch size={14} /> Zugangsdaten stehen NICHT in FORQ. Hier steht nur der Name des
            Eintrags in der Serverkonfiguration; der Wert liegt auf dem FORQ-Server und verlässt ihn nie.
          </p>

          <div className="parts-connections">
            {connections.map((connection) => (
              <article className="parts-connection" key={connection.id}>
                <header>
                  <h3>{supplierProviderLabels[connection.provider]}</h3>
                  <StatusPill
                    label={connection.active ? 'Aktiv' : 'Abgeschaltet'}
                    tone={connection.active ? 'done' : 'neutral'}
                  />
                </header>
                <dl>
                  <div>
                    <dt>Zugangsdaten</dt>
                    <dd>{connection.credentialRef || 'nicht hinterlegt'}</dd>
                  </div>
                  <div>
                    <dt>Letzte erfolgreiche Verbindung</dt>
                    <dd>
                      {connection.lastConnectedAt
                        ? new Date(connection.lastConnectedAt).toLocaleString('de-DE')
                        : 'noch nie'}
                    </dd>
                  </div>
                  <div>
                    <dt>Konfigurationsstand</dt>
                    <dd>Fassung {connection.configVersion}</dd>
                  </div>
                  <div>
                    <dt>Preisabweichung ab</dt>
                    <dd>
                      {connection.priceTolerancePercent} % und{' '}
                      {money(connection.priceToleranceAbsolute)} · {connection.leadTimeToleranceDays} Tage
                    </dd>
                  </div>
                  <div>
                    <dt>Sitzungsdauer</dt>
                    <dd>{Math.round(connection.sessionTtlSeconds / 60)} Minuten</dd>
                  </div>
                </dl>
                {connection.lastError ? (
                  <p className="parts-connection__error">{connection.lastError}</p>
                ) : null}
                <div className="parts-connection__switches">
                  <label>
                    <input
                      type="checkbox"
                      checked={connection.active}
                      disabled={!mayAdmin || busy !== null}
                      onChange={(event) =>
                        void toggleConnection(connection, { active: event.target.checked })
                      }
                    />
                    Anbindung aktiv
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={connection.punchoutEnabled}
                      disabled={!mayAdmin || busy !== null}
                      onChange={(event) =>
                        void toggleConnection(connection, { punchoutEnabled: event.target.checked })
                      }
                    />
                    Artikelsuche (PunchOut)
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={connection.orderingEnabled}
                      disabled={!mayAdmin || busy !== null}
                      onChange={(event) =>
                        void toggleConnection(connection, { orderingEnabled: event.target.checked })
                      }
                    />
                    Elektronische Bestellung
                  </label>
                </div>
                {connection.provider !== 'mock' && !connection.orderingEnabled ? (
                  <p className="parts-connection__gap">
                    Solange keine freigegebene Schnittstellendokumentation vorliegt, bleibt die
                    elektronische Bestellung abgeschaltet. Was dafür fehlt, steht in
                    <code> docs/integration/TVH-TREX-CHECKLISTE.md</code>.
                  </p>
                ) : null}
              </article>
            ))}
          </div>
        </section>
      ) : null}
    </WorkspacePage>
  );
}

/** Die Lieferanten in fester Reihenfolge -- für Auswahlfelder außerhalb dieser Seite. */
export const partsProviderOrder: readonly SupplierProvider[] = ['mock', 'tvh', 'trex'];
